import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../constants/common'
import {
  useInfiniteItemsListLoader,
  useInitData,
  useIsExternalContactProfileNGAvailable,
  useIsProviderProfileNGAvailable,
  useIsUserProfileNGAvailable,
  useOutboundUuid,
  useTimeZone,
} from '../../../hooks'
import { EntityProfileTabUpdates } from '../../Entity'
import { useRequestProfileContext } from '../RequestProfile.hooks'

import { getRequestLogEvents } from './RequestProfileTabUpdates.api'
import { REQUEST_LOG_EVENTS_PAGE_SIZE } from './RequestProfileTabUpdates.constants'
import {
  toKnownRequestLogEvents,
  toTranslatedUpdates,
} from './RequestProfileTabUpdates.utils'

function RequestProfileTabUpdates() {
  const { t } = useTranslation()

  const { availableSections } = useInitData()
  const isExternalContactProfileNGAvailable =
    useIsExternalContactProfileNGAvailable()
  const isProviderProfileNGAvailable = useIsProviderProfileNGAvailable()
  const isUserProfileNGAvailable = useIsUserProfileNGAvailable()

  const outboundUuid = useOutboundUuid()
  const timeZone = useTimeZone()

  const { requestData } = useRequestProfileContext()

  const requestId = requestData?.id
  const requestUpdatedAt = requestData?.updatedAt

  const {
    getThresholdItemProps,
    hasError,
    items,
    loadingInitial,
    loadingMore,
    updateListData,
  } = useInfiniteItemsListLoader({
    apiParams: {
      outboundUuid,
      page_size: REQUEST_LOG_EVENTS_PAGE_SIZE,
      request_id: requestId,
    },
    getData: getRequestLogEvents,
    loadPermission: Boolean(requestId),
    toPreparedItems: toKnownRequestLogEvents,
    withPagination: true,
  })

  const loadedUpdatedAtRef = useRef(requestUpdatedAt)

  useEffect(() => {
    // Skip the initial undefined -> loaded transition (the loader already fetches
    // once requestId is available); reload only on later edits of a loaded request
    // (a falsy timestamp means no requestId yet, so a reload would hit /request/undefined).
    const isInitialLoad = loadedUpdatedAtRef.current == null
    const hasChanged = requestUpdatedAt !== loadedUpdatedAtRef.current

    if (hasChanged) {
      loadedUpdatedAtRef.current = requestUpdatedAt

      if (!isInitialLoad && requestUpdatedAt) {
        updateListData()
      }
    }
  }, [requestUpdatedAt])

  const translatedItems = toTranslatedUpdates({
    items: items ?? EMPTY_LIST,
    linkContext: {
      availableSections,
      isExternalContactProfileNGAvailable,
      isProviderProfileNGAvailable,
      isUserProfileNGAvailable,
    },
    outboundUuid,
    t,
    timeZone,
  })

  // The auto-advance past filtered-to-empty pages runs as `loadingMore` with no
  // items yet — show the initial loader then, not the empty state.
  const isLoadingEmptyList = loadingMore && translatedItems.length === 0

  return (
    <EntityProfileTabUpdates
      getThresholdItemProps={getThresholdItemProps}
      hasError={hasError}
      isLoading={loadingInitial || isLoadingEmptyList}
      isLoadingMore={loadingMore}
      items={translatedItems}
    />
  )
}

export default RequestProfileTabUpdates
